import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * The biller's explicit "submit to the clearinghouse" action — THE submission trigger
 * (the note-locked/marked-billed auto-triggers are retired). Enqueues a durable
 * `claim-submit` task run; execution is asynchronous, and the outcome lands on the case
 * row (claim_submitted_at on success, claim_status='failed' + submit_last_error on a
 * clearinghouse refusal), which the list refetch picks up.
 *
 * The handler refuses ineligible cases up front — a clear answer for the button instead
 * of enqueueing a run whose own guards would quietly decline. The task re-checks
 * everything anyway; this pre-check is UX, not the safety boundary.
 */
export const billingV2SubmitClaim = oc
  .route({ method: 'POST', path: '/v2/billing/case/submit-claim' })
  .input(
    z.object({
      encounterId: z.string().min(1),
      /**
       * Submit under this practitioner instead of the Claim's provider — the clinic's
       * enrolled doctor when someone else rendered the visit. A pick for this run only: it
       * travels in the task's args and is journaled with the submission; nothing on the
       * case or the Claim changes, so a re-submit after a failure starts from the default.
       * Must practise in the case's organization.
       */
      submitAsPractitionerId: z.string().min(1).optional(),
    }),
  )
  .output(
    z.object({
      enqueued: z.boolean(),
      /**
       * Present when enqueued=false: CASE_NOT_FOUND, ROUTE_NOT_EXTERNAL,
       * ALREADY_SUBMITTED, NOT_SUBMITTABLE:<status>, PRACTITIONER_NOT_IN_ORGANIZATION, or
       * the Claim's content problems as sentences joined by "; " (PD-2013).
       */
      reason: z.string().optional(),
    }),
  )
